/**
 * Multi-Business Management Landing Page
 * SEO-optimized landing page for the Commercial Admin Portal
 */

import React from 'react'
import { Button, Row, Col, Card, Typography, Space, Divider } from 'antd'
import {
  GlobalOutlined,
  BarChartOutlined,
  TeamOutlined,
  SafetyOutlined,
  RocketOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  LineChartOutlined,
  ThunderboltOutlined,
  CloudOutlined
} from '@ant-design/icons'

const { Title, Paragraph, Text } = Typography

interface LandingPageProps {
  onGetStarted: () => void
}

export const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted }) => {
  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '0'
    }}>
      {/* Hero Section */}
      <div style={{ 
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        padding: '40px 24px',
        textAlign: 'center',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <GlobalOutlined style={{ fontSize: 72, color: '#fff' }} />
          <Title level={1} style={{ color: '#fff', marginBottom: 0, fontSize: '3rem' }}>
            Multi-Business Management
          </Title>
          <Title level={3} style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 400, maxWidth: 800, margin: '0 auto' }}>
            Manage Multiple Businesses from One Powerful Dashboard
          </Title>
          <Paragraph style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '1.1rem', maxWidth: 700, margin: '20px auto' }}>
            Scale your operations with enterprise-grade multi-business management. 
            Centralized control, aggregated analytics, and seamless business switching - all in one place.
          </Paragraph>
          <Space size="large">
            <Button 
              type="primary" 
              size="large" 
              icon={<RocketOutlined />}
              onClick={onGetStarted}
              style={{ 
                height: 50, 
                fontSize: '1.1rem',
                background: '#fff',
                color: '#667eea',
                border: 'none',
                fontWeight: 600
              }}
            >
              Get Started
            </Button>
            <Button 
              size="large"
              href="https://serviceflow-pro.com"
              style={{ 
                height: 50, 
                fontSize: '1.1rem',
                background: 'transparent',
                color: '#fff',
                borderColor: '#fff',
                fontWeight: 600
              }}
            >
              Learn More
            </Button>
          </Space>
        </Space>
      </div>

      {/* Features Section */}
      <div style={{ padding: '80px 24px', maxWidth: 1200, margin: '0 auto' }}>
        <Title level={2} style={{ textAlign: 'center', color: '#fff', marginBottom: 60 }}>
          Enterprise Features for Growing Businesses
        </Title>
        
        <Row gutter={[32, 32]}>
          <Col xs={24} md={8}>
            <Card 
              hoverable
              style={{ 
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                borderRadius: 12
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <BarChartOutlined style={{ fontSize: 48, color: '#667eea' }} />
                <Title level={4}>Aggregated Analytics</Title>
                <Paragraph>
                  View consolidated metrics across all your businesses. Track revenue, 
                  jobs, customers, and performance from a single dashboard.
                </Paragraph>
                <ul style={{ paddingLeft: 20 }}>
                  <li>Real-time business metrics</li>
                  <li>Revenue trends and forecasting</li>
                  <li>Cross-business comparisons</li>
                  <li>Custom date range analysis</li>
                </ul>
              </Space>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card 
              hoverable
              style={{ 
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                borderRadius: 12
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <ThunderboltOutlined style={{ fontSize: 48, color: '#667eea' }} />
                <Title level={4}>Seamless Business Switching</Title>
                <Paragraph>
                  Switch between businesses instantly without logging out. 
                  Maintain separate contexts while staying connected.
                </Paragraph>
                <ul style={{ paddingLeft: 20 }}>
                  <li>One-click business switching</li>
                  <li>Persistent authentication</li>
                  <li>Context-aware permissions</li>
                  <li>Business-specific settings</li>
                </ul>
              </Space>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card 
              hoverable
              style={{ 
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                borderRadius: 12
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <TeamOutlined style={{ fontSize: 48, color: '#667eea' }} />
                <Title level={4}>User Management</Title>
                <Paragraph>
                  Grant team members access to specific businesses with role-based 
                  permissions. Maintain security while enabling collaboration.
                </Paragraph>
                <ul style={{ paddingLeft: 20 }}>
                  <li>Role-based access control</li>
                  <li>Business-specific permissions</li>
                  <li>User invitation system</li>
                  <li>Audit logging</li>
                </ul>
              </Space>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card 
              hoverable
              style={{ 
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                borderRadius: 12
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <CloudOutlined style={{ fontSize: 48, color: '#667eea' }} />
                <Title level={4}>Integration Inheritance</Title>
                <Paragraph>
                  Configure integrations once and inherit them across businesses. 
                  Save time and ensure consistency.
                </Paragraph>
                <ul style={{ paddingLeft: 20 }}>
                  <li>Shared integration settings</li>
                  <li>Centralized configuration</li>
                  <li>Override when needed</li>
                  <li>Automatic synchronization</li>
                </ul>
              </Space>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card 
              hoverable
              style={{ 
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                borderRadius: 12
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <LineChartOutlined style={{ fontSize: 48, color: '#667eea' }} />
                <Title level={4}>Corporate Reporting</Title>
                <Paragraph>
                  Generate comprehensive reports across your business portfolio. 
                  Make data-driven decisions with confidence.
                </Paragraph>
                <ul style={{ paddingLeft: 20 }}>
                  <li>Portfolio-wide insights</li>
                  <li>Performance benchmarking</li>
                  <li>Custom report generation</li>
                  <li>Export capabilities</li>
                </ul>
              </Space>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card 
              hoverable
              style={{ 
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                borderRadius: 12
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <SafetyOutlined style={{ fontSize: 48, color: '#667eea' }} />
                <Title level={4}>Enterprise Security</Title>
                <Paragraph>
                  Bank-level security with JWT authentication, encrypted data, 
                  and comprehensive audit trails.
                </Paragraph>
                <ul style={{ paddingLeft: 20 }}>
                  <li>Encrypted communications</li>
                  <li>Secure token management</li>
                  <li>Activity monitoring</li>
                  <li>Compliance ready</li>
                </ul>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Benefits Section */}
      <div style={{ 
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        padding: '80px 24px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Title level={2} style={{ textAlign: 'center', color: '#fff', marginBottom: 60 }}>
            Why Choose Multi-Business Management?
          </Title>
          
          <Row gutter={[48, 48]}>
            <Col xs={24} md={12}>
              <Space direction="vertical" size="large">
                <Space>
                  <CheckCircleOutlined style={{ fontSize: 32, color: '#4ade80' }} />
                  <div>
                    <Title level={4} style={{ color: '#fff', marginBottom: 8 }}>
                      Scale Effortlessly
                    </Title>
                    <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '1rem' }}>
                      Grow from one business to dozens without complexity. Our platform scales with you.
                    </Text>
                  </div>
                </Space>

                <Space>
                  <CheckCircleOutlined style={{ fontSize: 32, color: '#4ade80' }} />
                  <div>
                    <Title level={4} style={{ color: '#fff', marginBottom: 8 }}>
                      Save Time
                    </Title>
                    <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '1rem' }}>
                      Manage all businesses from one place. No more logging in and out of multiple accounts.
                    </Text>
                  </div>
                </Space>

                <Space>
                  <CheckCircleOutlined style={{ fontSize: 32, color: '#4ade80' }} />
                  <div>
                    <Title level={4} style={{ color: '#fff', marginBottom: 8 }}>
                      Better Insights
                    </Title>
                    <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '1rem' }}>
                      See the big picture with aggregated analytics and cross-business comparisons.
                    </Text>
                  </div>
                </Space>
              </Space>
            </Col>

            <Col xs={24} md={12}>
              <Space direction="vertical" size="large">
                <Space>
                  <CheckCircleOutlined style={{ fontSize: 32, color: '#4ade80' }} />
                  <div>
                    <Title level={4} style={{ color: '#fff', marginBottom: 8 }}>
                      Reduce Costs
                    </Title>
                    <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '1rem' }}>
                      Share integrations and configurations across businesses to minimize setup time and costs.
                    </Text>
                  </div>
                </Space>

                <Space>
                  <CheckCircleOutlined style={{ fontSize: 32, color: '#4ade80' }} />
                  <div>
                    <Title level={4} style={{ color: '#fff', marginBottom: 8 }}>
                      Maintain Control
                    </Title>
                    <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '1rem' }}>
                      Each business maintains its own data and settings while you maintain oversight.
                    </Text>
                  </div>
                </Space>

                <Space>
                  <CheckCircleOutlined style={{ fontSize: 32, color: '#4ade80' }} />
                  <div>
                    <Title level={4} style={{ color: '#fff', marginBottom: 8 }}>
                      Enterprise Ready
                    </Title>
                    <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '1rem' }}>
                      Built for scale with security, compliance, and performance in mind.
                    </Text>
                  </div>
                </Space>
              </Space>
            </Col>
          </Row>
        </div>
      </div>

      {/* Pricing Section */}
      <div style={{ padding: '80px 24px', maxWidth: 1200, margin: '0 auto' }}>
        <Title level={2} style={{ textAlign: 'center', color: '#fff', marginBottom: 60 }}>
          Simple, Transparent Pricing
        </Title>
        
        <Row gutter={[32, 32]} justify="center">
          <Col xs={24} md={10}>
            <Card 
              style={{ 
                height: '100%',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                borderRadius: 12,
                textAlign: 'center'
              }}
            >
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <DollarOutlined style={{ fontSize: 48, color: '#667eea' }} />
                <Title level={3}>Multi-Business Add-On</Title>
                <div>
                  <Title level={2} style={{ marginBottom: 0 }}>
                    $49<Text style={{ fontSize: '1rem', fontWeight: 400 }}>/month</Text>
                  </Title>
                  <Text type="secondary">per additional business</Text>
                </div>
                <Divider />
                <Space direction="vertical" size="small" style={{ textAlign: 'left', width: '100%' }}>
                  <Text><CheckCircleOutlined style={{ color: '#4ade80', marginRight: 8 }} />Unlimited business switching</Text>
                  <Text><CheckCircleOutlined style={{ color: '#4ade80', marginRight: 8 }} />Aggregated analytics dashboard</Text>
                  <Text><CheckCircleOutlined style={{ color: '#4ade80', marginRight: 8 }} />Integration inheritance</Text>
                  <Text><CheckCircleOutlined style={{ color: '#4ade80', marginRight: 8 }} />Corporate reporting</Text>
                  <Text><CheckCircleOutlined style={{ color: '#4ade80', marginRight: 8 }} />User management</Text>
                  <Text><CheckCircleOutlined style={{ color: '#4ade80', marginRight: 8 }} />Priority support</Text>
                </Space>
                <Button 
                  type="primary" 
                  size="large" 
                  block
                  onClick={onGetStarted}
                  style={{ marginTop: 20 }}
                >
                  Get Started
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* CTA Section */}
      <div style={{ 
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        padding: '80px 24px',
        textAlign: 'center',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Title level={2} style={{ color: '#fff' }}>
            Ready to Scale Your Business?
          </Title>
          <Paragraph style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '1.1rem', maxWidth: 600, margin: '0 auto' }}>
            Join hundreds of growing businesses managing their operations with ServiceFlow Pro's 
            Multi-Business Management platform.
          </Paragraph>
          <Space size="large">
            <Button 
              type="primary" 
              size="large" 
              icon={<RocketOutlined />}
              onClick={onGetStarted}
              style={{ 
                height: 50, 
                fontSize: '1.1rem',
                background: '#fff',
                color: '#667eea',
                border: 'none',
                fontWeight: 600
              }}
            >
              Start Free Trial
            </Button>
            <Button 
              size="large"
              href="https://serviceflow-pro.com/contact"
              style={{ 
                height: 50, 
                fontSize: '1.1rem',
                background: 'transparent',
                color: '#fff',
                borderColor: '#fff',
                fontWeight: 600
              }}
            >
              Contact Sales
            </Button>
          </Space>
        </Space>
      </div>

      {/* Footer */}
      <div style={{ 
        background: 'rgba(0, 0, 0, 0.2)',
        padding: '40px 24px',
        textAlign: 'center',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Space split={<Divider type="vertical" style={{ borderColor: 'rgba(255, 255, 255, 0.3)' }} />}>
          <a href="https://serviceflow-pro.com" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
            Main Website
          </a>
          <a href="https://serviceflow-pro.com/product" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
            Products
          </a>
          <a href="https://serviceflow-pro.com/pricing" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
            Pricing
          </a>
          <a href="https://serviceflow-pro.com/contact" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
            Contact
          </a>
        </Space>
        <Divider style={{ borderColor: 'rgba(255, 255, 255, 0.1)', margin: '24px 0' }} />
        <Text style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          © 2025 ServiceFlow Pro. All rights reserved.
        </Text>
      </div>
    </div>
  )
}

